import React, { useMemo } from 'react';
import { Divider, Grid2 } from '@mui/material';
import { BlogHOC } from '@components/BlogSection/BlogComponents/HOC/BlogHOC';
import { BlogContentArea } from '@components/BlogSection/BlogComponents/BlogPage/BlogContentArea';
import { Redirect } from '@components/Redirect';
import { getTranslation } from '@helpers/languageTranslations';
import { useTranslation } from '@stateManagement/TranslationContext';
import { useStaticValues } from '@stateManagement/StaticValuesContext';
import { breakpoints, Heading, LazyLoadContainer, v2Colors, Container } from '@web-for-marketing/react-ui';
import { BlogHeader } from '@components/BlogSection/BlogComponents/BlogPage/BlogHeader';
import { useAtom } from 'jotai';
import { BlogCard2 } from '@components/BlogCards/BlogCard2';
import { selectedLanguageAtom } from '@atoms/appSettings';
import { getLanguageRouteAndPrefixByCodeAndRouteKey } from '@helpers/languages';
import { LanguageCode } from '@models/languageCode';
import { languageRouteTypes } from '@helpers/languageRoutes';
import { TableOfContentsMobile } from '@components/TableOfContents/TableOfContentsMobileV2';

function generateBlogLink(selectedLanguage: LanguageCode, routeKey: 'categoryUrl' | 'blogHomeUrl'): string {
    return getLanguageRouteAndPrefixByCodeAndRouteKey(selectedLanguage, routeKey);
}

const classes = {
    relatedPostBackground: {
        margin: '0',
        backgroundColor: v2Colors.background.lighter,
        [`@media (max-width: ${breakpoints.md})`]: {
            padding: 0,
            boxShadow: 'none',
            flexDirection: 'column-reverse',
        },
        [`@media (max-width: ${breakpoints.md})`]: {
            '&:before': {
                content: 'none',
            },
        },
    },
    relatedPostContainer: {
        padding: '5.6rem 6.4rem',
        [`@media (max-width: ${breakpoints.lg})`]: {
            padding: '5.6rem 0',
        },
        [`@media (max-width: ${breakpoints.sm})`]: {
            padding: '1.6rem 0',
        },
    },
    container: {
        padding: '0 6.4rem',
        [`@media (max-width: ${breakpoints.lg})`]: {
            padding: '0',
        },
    },
    relatedPostTitle: {
        marginBottom: '3.2rem',
        [`@media (max-width: ${breakpoints.sm})`]: {
            marginBottom: '1.6rem',
        },
    },
    blogHeaderBackgroundContainer: {
        paddingTop: '1.2rem',
        backgroundColor: v2Colors.background.light,
        [`@media (max-width: ${breakpoints.lg})`]: {
            backgroundColor: v2Colors.background.white,
            paddingTop: 0,
        },
    },
    dividerContainer: {
        display: 'block',
        [`@media (max-width: ${breakpoints.md})`]: {
            display: 'none',
        },
    },
    tableOfContentsMobileContainer: {
        display: 'none',
        [`@media (max-width: ${breakpoints.md})`]: {
            display: 'block',
            marginBottom: '2.4rem',
        },
    },
    divider: {
        borderWidth: '1.5px',
    },
} as const;

export default function PublicBlog(): JSX.Element | null {
    const {
        blog: { articleData },
        prefixStringWithBaseUrl,
    } = useStaticValues();
    const [selectedLanguage] = useAtom(selectedLanguageAtom);

    const { selectedLanguageTranslation } = useTranslation();
    const blogPostTranslation = useMemo(
        () => getTranslation(selectedLanguageTranslation, 'blogPost'),
        [selectedLanguageTranslation]
    );

    const categoryBlogLink = useMemo(
        () => generateBlogLink(selectedLanguage, languageRouteTypes.CATEGORY),
        [selectedLanguage]
    );

    const languageBlogLink = useMemo(
        () => generateBlogLink(selectedLanguage, languageRouteTypes.BLOG_HOME),
        [selectedLanguage]
    );

    return articleData ? (
        articleData?.blogArticle.redirectUrl ? (
            <Redirect statusCode={200} redirectUrl={articleData.blogArticle.redirectUrl} />
        ) : (
            <BlogHOC type='articlePage' noIndex={articleData?.blogArticle.noIndex}>
                <div css={classes.blogHeaderBackgroundContainer}>
                    <BlogHeader blogPost={articleData.blogArticle} categoryBlogLink={categoryBlogLink} />
                </div>
                <Container>
                    <div css={classes.container}>
                        <div css={classes.dividerContainer}>
                            <Divider css={classes.divider}></Divider>
                        </div>
                    </div>
                </Container>
                <TableOfContentsMobile
                    content={articleData.blogArticle.htmlContent}
                    id='blogtoc'
                    css={classes.tableOfContentsMobileContainer}
                />
                <Container>
                    <div css={classes.container}>
                        <BlogContentArea blogPost={articleData.blogArticle} />
                    </div>
                </Container>
                <div css={classes.relatedPostBackground}>
                    <Container component='section'>
                        <div css={classes.relatedPostContainer}>
                            <Heading variant='h2' color='activePrimary' css={classes.relatedPostTitle}>
                                {blogPostTranslation.relatedPosts}
                            </Heading>
                            <LazyLoadContainer component={Grid2} spacing={{ xs: 2, md: 4 }} container>
                                {articleData.otherPosts.map((card) => (
                                    <Grid2 key={card.id} size={{ xs: 12, md: 6 }}>
                                        <BlogCard2
                                            title={card.title}
                                            linkPath={`${languageBlogLink}${card.slug}`}
                                            image={{
                                                imagePath: prefixStringWithBaseUrl(card.cardImageUrl),
                                                imageAlt: card.cardImageAlt,
                                            }}
                                            category={card.category.name}
                                            categoryLink={`${categoryBlogLink}${card.category.slug}`}
                                            publishedDateUtc={card.publishedDateUtc ? card.publishedDateUtc : ''}
                                            readMinutes={card.readMinutes}
                                            ariaLabel={`Read more about ${card.title}`}
                                            lazyLoad={true}
                                        />
                                    </Grid2>
                                ))}
                            </LazyLoadContainer>
                        </div>
                    </Container>
                </div>
            </BlogHOC>
        )
    ) : null;
}
